import { useState } from 'react'
import { Link } from 'react-router-dom'
import { DiceIcon } from '../components/DiceControls.tsx'
import { SharedRow } from '../components/SharedAnnotation.tsx'
import { friendPrefix } from '../domain/friendName.ts'
import { findConcert } from '../domain/program.ts'
import { t } from '../i18n.ts'
import { CONCERTS } from '../program.ts'
import { sharedKey } from '../store/annotations.ts'
import { useAuth } from '../store/AuthProvider.tsx'
import { useAllAnnotations, useFriends, useStore } from '../store/StoreProvider.tsx'
import type { Annotation, Friend } from '../store/types.ts'
import './Friends.css'

/**
 * Who you are connected to, and everything that has passed between you.
 *
 * Four groups, in the order the reader owes them attention. Incoming requests come
 * first because they are the only thing on the page waiting on an answer — until one
 * is given, neither party sees a single note of the other's (R29). Then the requests
 * the reader sent, which are waiting on someone else. Then the friends themselves with
 * the annotations exchanged (R11). And last, shares from people who are *not* friends
 * (R34) — the group that exists because a share arrives whether or not a friendship
 * was ever accepted, and because this page absorbed «Delt med meg», which used to be
 * the only place those could be read.
 *
 * Nothing here writes to the friend store. Every mutation goes through `/api/friends`,
 * which needs privileged access to resolve an address to a uid, and comes back through
 * the Firestore listener as a fresh snapshot. What this page keeps locally is only the
 * outcome of an action it has already had confirmed — see `resolved` below.
 */
export function Friends() {
  const store = useStore()
  const list = useFriends()
  const { cloudEnabled, user, getIdToken } = useAuth()
  // Subscribes to annotation writes; the lists themselves are read off the store.
  useAllAnnotations()

  const [email, setEmail] = useState('')
  const [form, setForm] = useState<FormState>({ kind: 'idle' })
  /*
   * What the server has already confirmed but the listener has not delivered yet.
   *
   * Without it, accepting a request would post successfully and leave the request
   * sitting exactly where it was — the friend list only changes when Firestore pushes
   * a new snapshot, and offline, or in a test, that never comes. Entries naming a uid
   * the snapshot no longer carries are simply ignored, so the overlay cannot outlive
   * the truth it anticipates.
   */
  const [resolved, setResolved] = useState<Record<string, 'accepted' | 'gone'>>({})
  const [confirming, setConfirming] = useState<Confirmation | null>(null)
  const [busyUid, setBusyUid] = useState<string | null>(null)
  const [actionFailed, setActionFailed] = useState(false)

  const signedIn = Boolean(user)
  const needsSignIn = cloudEnabled && !user

  const entries = list
    .filter((friend) => resolved[friend.uid] !== 'gone')
    .map((friend) =>
      resolved[friend.uid] === 'accepted' ? { ...friend, status: 'accepted' as const } : friend,
    )

  const incoming = entries.filter((friend) => friend.status === 'incoming')
  const outgoing = entries.filter((friend) => friend.status === 'outgoing')
  const accepted = entries.filter((friend) => friend.status === 'accepted')
  const acceptedUids = new Set(accepted.map((friend) => friend.uid))

  // Restricted to the committed programme on purpose: an annotation of a concert this
  // build has never heard of has no name to render and nowhere to link.
  const shared = CONCERTS.flatMap((concert) => store.sharedAnnotations(concert.id))
  /*
   * The keys of everything that arrived through an explicit share rather than through
   * the friend feed. `sharedFrom` is the only place the two are still distinguishable:
   * `sharedAnnotations` folds them into one deduped list, which is what every other
   * reader wants and precisely what R34's marking cannot use.
   */
  const explicitlyShared = new Set(store.sharedFrom('shared').map(sharedKey))
  const own = store.allAnnotations()

  const fromStrangers = shared.filter((annotation) => !acceptedUids.has(annotation.uid ?? ''))

  const isEmpty =
    incoming.length === 0 &&
    outgoing.length === 0 &&
    accepted.length === 0 &&
    fromStrangers.length === 0

  /**
   * Everything exchanged with one friend that is worth reading: theirs first, then the
   * reader's own.
   *
   * Worth reading is narrower than «not blank», and deliberately so — see `hasContent`.
   */
  function entriesFor(friend: Friend): Entry[] {
    const theirs = shared
      .filter((annotation) => annotation.uid === friend.uid && hasContent(annotation))
      .map((annotation) => ({
        key: sharedKey(annotation),
        concertId: annotation.concertId,
        by: t.friends.by(friendPrefix(friend)),
        note: annotation.note,
        dice: annotation.dice ?? null,
        explicit: explicitlyShared.has(sharedKey(annotation)),
      }))

    const mine = own
      .filter((annotation) => annotation.sharedWith.includes(friend.uid) && hasContent(annotation))
      .map((annotation) => ({
        key: `mine:${annotation.concertId}`,
        concertId: annotation.concertId,
        by: t.friends.byYou,
        note: annotation.note,
        dice: annotation.dice ?? null,
        explicit: false,
      }))

    return [...theirs, ...mine]
  }

  async function act(path: string, uid: string, effect: 'accepted' | 'gone') {
    setBusyUid(uid)
    setActionFailed(false)
    const { outcome } = await postFriends(path, { uid }, getIdToken)
    setBusyUid(null)
    setConfirming(null)
    if (outcome === 'ok') setResolved((prev) => ({ ...prev, [uid]: effect }))
    else setActionFailed(true)
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setForm({ kind: 'sending' })
    const { outcome, notified } = await postFriends('/api/friends', { email }, getIdToken)
    if (outcome === 'ok') {
      setForm({ kind: 'sent', notified })
      setEmail('')
      return
    }
    // The address stays in the field. A refused request is the moment the user is most
    // likely to try again, and making them retype it is the surest way to lose them.
    setForm({ kind: 'failed', reason: outcome })
  }

  return (
    <div className="page friends-page">
      <h1 className="friends-page__title">{t.friends.title}</h1>

      {signedIn ? (
        <section className="friends-page__add" aria-label={t.friends.addTitle}>
          <h2 className="friends-page__add-title">{t.friends.addTitle}</h2>
          <p className="friends-page__add-help">{t.friends.addHelp}</p>
          <form className="friends-page__form" onSubmit={submit}>
            <label className="sr-only" htmlFor="friend-email">
              {t.friends.emailLabel}
            </label>
            <input
              id="friend-email"
              className="friends-page__input"
              type="email"
              required
              inputMode="email"
              autoComplete="email"
              placeholder={t.friends.emailPlaceholder}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                if (form.kind !== 'idle') setForm({ kind: 'idle' })
              }}
            />
            <button
              type="submit"
              className="friends-page__submit"
              disabled={form.kind === 'sending'}
            >
              {form.kind === 'sending' ? t.friends.sending : t.friends.add}
            </button>
          </form>

          {form.kind === 'sent' && (
            <p className="friends-page__status friends-page__status--ok" role="status">
              {/* What actually happened, not what usually happens: the server reports
                  whether the invitation was mailed, and an unsent one leaves the sender
                  as the only person who can tell their friend to look. */}
              {form.notified ? t.friends.sent : t.friends.sentSilent}
            </p>
          )}
          {form.kind === 'failed' && (
            <p className="friends-page__status friends-page__status--error" role="status">
              {form.reason === 'rate-limited' ? t.friends.rateLimited : t.friends.failed}
            </p>
          )}
        </section>
      ) : (
        <p className="friends-page__hint">
          {cloudEnabled ? t.friends.signInHelp : t.friends.localOnly}
        </p>
      )}

      {actionFailed && (
        <p className="friends-page__status friends-page__status--error" role="status">
          {t.friends.actionFailed}
        </p>
      )}

      {incoming.length > 0 && (
        <section className="friends-page__group" aria-label={t.friends.incomingTitle}>
          <h2 className="friends-page__group-title">{t.friends.incomingTitle}</h2>
          <ul className="friends-page__list">
            {incoming.map((friend) => (
              <li className="friends-page__person" key={friend.uid}>
                <PersonName friend={friend} />
                {/* R27, where the consequence lands: what accepting hands over, in
                    front of the button that hands it over. */}
                <p className="friends-page__disclosure">{t.friends.acceptDiscloses}</p>
                {confirming?.uid === friend.uid && confirming.kind === 'decline' ? (
                  <>
                    <p className="friends-page__warning">{t.friends.declineWarning}</p>
                    <div className="friends-page__actions">
                      <button
                        type="button"
                        className="friends-page__danger"
                        disabled={busyUid === friend.uid}
                        onClick={() => void act('/api/friends/decline', friend.uid, 'gone')}
                      >
                        {t.friends.declineConfirm}
                      </button>
                      <button
                        type="button"
                        className="friends-page__secondary"
                        onClick={() => setConfirming(null)}
                      >
                        {t.friends.cancel}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="friends-page__actions">
                    <button
                      type="button"
                      className="friends-page__primary"
                      disabled={busyUid === friend.uid}
                      onClick={() => void act('/api/friends/accept', friend.uid, 'accepted')}
                    >
                      {t.friends.accept}
                    </button>
                    <button
                      type="button"
                      className="friends-page__secondary"
                      onClick={() => setConfirming({ uid: friend.uid, kind: 'decline' })}
                    >
                      {t.friends.decline}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {outgoing.length > 0 && (
        <section className="friends-page__group" aria-label={t.friends.outgoingTitle}>
          <h2 className="friends-page__group-title">{t.friends.outgoingTitle}</h2>
          <ul className="friends-page__list">
            {outgoing.map((friend) => (
              <li className="friends-page__person" key={friend.uid}>
                <PersonName friend={friend} />
                <p className="friends-page__awaiting">{t.friends.awaitingReply}</p>
                <div className="friends-page__actions">
                  <button
                    type="button"
                    className="friends-page__secondary"
                    disabled={busyUid === friend.uid}
                    onClick={() => void act('/api/friends/remove', friend.uid, 'gone')}
                  >
                    {t.friends.cancelRequest}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {accepted.length > 0 && (
        <section className="friends-page__group" aria-label={t.friends.friendsTitle}>
          <h2 className="friends-page__group-title">{t.friends.friendsTitle}</h2>
          <ul className="friends-page__list">
            {accepted.map((friend) => {
              const entries = entriesFor(friend)
              return (
                <li className="friends-page__person" key={friend.uid}>
                  <PersonName friend={friend} />

                  {entries.length === 0 ? (
                    <p className="friends-page__none">{t.friends.noEntries}</p>
                  ) : (
                    <ul className="friends-page__entries">
                      {entries.map((entry) => (
                        <EntryRow key={entry.key} entry={entry} />
                      ))}
                    </ul>
                  )}

                  {confirming?.uid === friend.uid && confirming.kind === 'remove' ? (
                    <>
                      <p className="friends-page__warning">{t.friends.removeWarning}</p>
                      <div className="friends-page__actions">
                        <button
                          type="button"
                          className="friends-page__danger"
                          disabled={busyUid === friend.uid}
                          onClick={() => void act('/api/friends/remove', friend.uid, 'gone')}
                        >
                          {t.friends.removeConfirm}
                        </button>
                        <button
                          type="button"
                          className="friends-page__secondary"
                          onClick={() => setConfirming(null)}
                        >
                          {t.friends.cancel}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="friends-page__actions">
                      <button
                        type="button"
                        className="friends-page__secondary"
                        onClick={() => setConfirming({ uid: friend.uid, kind: 'remove' })}
                      >
                        {t.friends.remove}
                      </button>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      )}

      {fromStrangers.length > 0 && (
        <section className="friends-page__group" aria-label={t.friends.sharedTitle}>
          <h2 className="friends-page__group-title">{t.friends.sharedTitle}</h2>
          <p className="friends-page__group-help">{t.friends.sharedHelp}</p>
          <ul className="friends-page__shared">
            {fromStrangers.map((annotation) => (
              <SharedRow
                key={sharedKey(annotation)}
                annotation={annotation}
                concertName={findConcert(CONCERTS, annotation.concertId)?.name ?? annotation.concertId}
              />
            ))}
          </ul>
        </section>
      )}

      {isEmpty && !needsSignIn && (
        <div className="friends-page__empty">
          <p>{t.friends.empty}</p>
          <p className="friends-page__empty-help">{t.friends.emptyHelp}</p>
        </div>
      )}
    </div>
  )
}

/**
 * Whether an annotation says anything this page is willing to spend a row on.
 *
 * Narrower than `isBlank`, which also admits a bare thumb — and a bare thumb is exactly
 * what this page cannot afford. Friendship shows every concert a friend has annotated,
 * not only the ones they sent (R12), and thumbing through the programme once is how
 * anybody uses the day list: one pass leaves thirty concerts marked. Each of those used
 * to land here as its own row carrying a concert name, «Fra Kari» and nothing else,
 * burying the handful of rows that had something in them under a list whose only
 * content was its own length.
 *
 * So the two things that still read at a distance from the concert they belong to earn
 * a row here, and the one that does not is dropped. A die is a verdict and a note is a
 * sentence; a thumb is a plan, and a plan is only legible next to the time and stage it
 * is a plan about — which is the day list, where it stays (`ConcertRow`), and the
 * concert page, which renders the full annotation either way.
 */
function hasContent(annotation: Annotation): boolean {
  return annotation.note.trim() !== '' || annotation.dice != null
}

/** The name a friend reads as, with the address that decided it underneath. */
function PersonName({ friend }: { friend: Friend }) {
  return (
    <>
      <h3 className="friends-page__name">{friendPrefix(friend)}</h3>
      {friend.email && <p className="friends-page__address">{friend.email}</p>}
    </>
  )
}

/**
 * One annotation exchanged with a friend.
 *
 * The mark sits outside the link deliberately: it describes how the entry reached the
 * reader, not where tapping it goes, and folding it into the link's accessible name
 * would have every row announce as «Candura Fra Kari Delt med deg».
 *
 * The die is inside it, for the mirror-image reason. It is not metadata about delivery
 * but half of what the row has to say, and «Candura, fra Kari, terningkast 5, må ses»
 * is the whole entry read aloud in the order it is written. It sits beside the name
 * that owns it rather than in a column of its own, the same way the day row places a
 * friend's die (`ConcertRow`) — a die alone in a trailing column reads as the reader's.
 */
function EntryRow({ entry }: { entry: Entry }) {
  const concert = findConcert(CONCERTS, entry.concertId)
  return (
    <li className="friend-entry">
      <Link to={`/konsert/${entry.concertId}`} className="friend-entry__link">
        <span className="friend-entry__name">{concert?.name ?? entry.concertId}</span>
        <span className="friend-entry__by">
          {entry.by}
          {entry.dice !== null && (
            <span className="friend-entry__dice" title={t.concert.diceValue(entry.dice)}>
              {/* The same face the concert page's control draws, at the size the row
                  wants: the list and the control must not disagree about a 5. */}
              <DiceIcon value={entry.dice} />
              <span className="sr-only">{t.concert.diceValue(entry.dice)}</span>
            </span>
          )}
        </span>
        {entry.note && <span className="friend-entry__note">{entry.note}</span>}
      </Link>
      {entry.explicit && <span className="friend-entry__mark">{t.friends.explicitlyShared}</span>}
    </li>
  )
}

interface Entry {
  key: string
  concertId: string
  by: string
  note: string
  /** Terningkast, 1–6, or null when the annotation carries only words. */
  dice: number | null
  /** True when the friend picked this one out and sent it, rather than merely wrote it. */
  explicit: boolean
}

interface Confirmation {
  uid: string
  kind: 'decline' | 'remove'
}

type FormState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; notified: boolean }
  | { kind: 'failed'; reason: Outcome }

type Outcome = 'ok' | 'rate-limited' | 'failed'

interface Result {
  outcome: Outcome
  /** Whether the server got an invitation email out. Never true for a failed call. */
  notified: boolean
}

/**
 * Every call this page makes, in one shape.
 *
 * The 15-second deadline is `ShareSheet`'s, for `ShareSheet`'s reason: the server can
 * legitimately spend a while, and without a deadline the button sits on «Sender …»
 * forever on a bad connection with no error and no way to retry. 429 is separated out
 * here rather than at each call site because the daily cap is the one failure the user
 * can do something about — namely wait.
 *
 * `notified` defaults to false rather than true, so a response this build cannot read —
 * an old server, a proxy's error page — degrades into the more modest of the two
 * confirmations instead of promising an email nobody sent.
 */
async function postFriends(
  path: string,
  body: Record<string, string>,
  getIdToken: () => Promise<string | null>,
): Promise<Result> {
  try {
    const token = await getIdToken()
    const response = await fetch(path, {
      method: 'POST',
      signal: AbortSignal.timeout(15_000),
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
      body: JSON.stringify(body),
    })
    if (response.status === 429) return { outcome: 'rate-limited', notified: false }
    if (!response.ok) return { outcome: 'failed', notified: false }

    let notified = false
    try {
      notified = ((await response.json()) as { notified?: boolean }).notified === true
    } catch {
      // The call succeeded; only the body was unreadable. Nothing to report but the
      // success itself.
    }
    return { outcome: 'ok', notified }
  } catch {
    return { outcome: 'failed', notified: false }
  }
}
